import React, { useContext, useMemo } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';

import { HelmetContext } from 'src/components/Helmet';
import ContentSection, { SupportedContentSectionTypes } from 'src/pages/hubs/shared/children/ContentSection';
import PageHeading from 'src/pages/hubs/shared/children/PageHeading';
import { useScreenEvent } from 'src/hooks/useScreenEvent';

import { RovoHubLoaderProps } from './loader';

export const RovoHubPage: React.FC = () => {
  const helmet = useContext(HelmetContext);

  const { rovoHubData } = useLoaderData() as RovoHubLoaderProps;

  helmet.setHelmet({
    title: `${rovoHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: rovoHubData.seo,
  });

  useScreenEvent({ eventName: 'rovoHubScreen' });

  const contentSections = useMemo(
    () =>
      rovoHubData.contentContainerCollection.items.map((contentBlock) => (
        <ContentSection key={(contentBlock as SupportedContentSectionTypes).sys.id} contentBlock={contentBlock} />
      )),
    [rovoHubData.contentContainerCollection.items],
  );

  return (
    <main data-testid="rovo-hub" css={pageContainerStyles}>
      <PageHeading
        title={rovoHubData.hero.titleExternal}
        subtitle={rovoHubData.hero.subtitle}
        keyword="Rovo"
        logoSrc={rovoHubData.hero.asset?.image.url || '/images/logos/rovo.svg'}
        logoAlt="Rovo logo"
        flagImageSrc="/images/hubs/rovoFlag.svg"
        bannerDesktopSrc={rovoHubData.hero.backgroundImageDesktop?.image.url || '/images/hubs/rovoHubBannerDesktop.svg'}
        bannerMobileSrc={rovoHubData.hero.backgroundImageMobile?.image.url || '/images/hubs/rovoHubBannerMobile.svg'}
        bannerMobileAlt="Rovo"
      />

      {/* content */}
      <div data-testid="rovo-hub-content-section" css={outerContentContainerStyles}>
        <div css={innerContentContainerStyles}>{contentSections}</div>
      </div>
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',
  gap: token('space.1000'),

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '& [data-testid="hero"]': {
    margin: '0 auto',
  },

  [media.below.md]: {
    gap: token('space.600'),
  },
});

const outerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.md]: {
    padding: `0 ${token('space.200')}`,
  },
});

const innerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: `calc(${token('space.1000')} * 2)`,
  alignSelf: 'center',

  [media.below.md]: {
    rowGap: `calc(${token('space.600')} * 2)`,
  },
});
